import { useEffect, useRef, useState } from 'react'
import { useJobStore } from '@/store/jobStore'
import { getWebSocketService } from '@/services/websocket'
import type { Job } from '@/types/job'
import { useQueryClient } from '@tanstack/react-query'

export function useJobWebSocket(jobId: string | undefined) {
  const { loadJob } = useJobStore()
  const queryClient = useQueryClient()
  const cleanupRef = useRef<(() => void) | null>(null)
  const [isConnected, setIsConnected] = useState(false)

  useEffect(() => {
    if (!jobId) {
      setIsConnected(false)
      return
    }

    const wsService = getWebSocketService()
    
    // Subscribe to connection status changes
    const statusCleanup = wsService.onStatusChange(() => {
      setIsConnected(wsService.isConnected(jobId))
    })

    // Connect to WebSocket (async)
    let cleanup: (() => void) | null = null
    
    wsService.connect(jobId, (updatedJob: Job) => {
      // Update job in store
      loadJob(jobId)
      
      // Invalidate React Query cache to trigger refetch
      queryClient.setQueryData(['jobs', jobId], updatedJob)
      queryClient.invalidateQueries({ queryKey: ['jobs', jobId] })
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
    }).then((cleanupFn) => {
      cleanup = cleanupFn
      cleanupRef.current = () => {
        if (cleanup) cleanup()
        statusCleanup()
      }
      // Set initial connection status
      setIsConnected(wsService.isConnected(jobId))
    }).catch((error) => {
      console.error('Failed to connect WebSocket:', error)
      setIsConnected(false)
    })

    return () => {
      if (cleanupRef.current) {
        cleanupRef.current()
      }
      setIsConnected(false)
    }
  }, [jobId, loadJob, queryClient])

  return {
    isConnected,
  }
}
